import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { RadioGroup, RadioGroupItem } from "design-system/atoms/RadioGroup"
import { FormControl, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { normalizeUrlPathname } from "lopermut"
import { Plus, Trash2 } from "lucide-react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { WidgetConfigurationFormType } from ".."

type DisplayUrlsProps = {
  form: UseFormReturn<WidgetConfigurationFormType>
}

export const DisplayUrls = ({ form }: DisplayUrlsProps) => {
  const { t } = useTranslation()
  const displayUrls = form.watch("displayUrls") ?? []
  const displayMode = form.watch("displayMode") ?? "all"

  const setDisplayUrls = (urls: string[]) => {
    form.setValue("displayUrls", urls, { shouldDirty: true, shouldValidate: true })
    form.setValue("displayMode", urls.length > 0 ? "custom" : "all", { shouldDirty: true })
  }

  return (
    <div className="flex flex-col gap-4">
      <RadioGroup
        value={displayMode}
        onValueChange={(value) => setDisplayUrls(value === "all" ? [] : ["/"])}
        className="grid grid-cols-2 gap-3"
      >
        {(["all", "custom"] as const).map((option) => (
          <label
            key={option}
            htmlFor={`displayUrls-${option}`}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
              displayMode === option
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/20 hover:border-muted-foreground/40",
            )}
          >
            <RadioGroupItem id={`displayUrls-${option}`} value={option} />
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-medium leading-snug">
                {t(`campaigns.templates.widget.config.displayUrls.options.${option}`)}
              </span>
              <span className="text-sm text-muted-foreground">
                {t(`campaigns.templates.widget.config.displayUrls.descriptions.${option}`)}
              </span>
            </div>
          </label>
        ))}
      </RadioGroup>

      {displayMode === "custom" && (
        <FormField
          control={form.control}
          name="displayUrls"
          render={() => (
            <FormItem>
              <FormControl>
                <div className="flex flex-col gap-2">
                  {displayUrls.map((url, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={url}
                        placeholder="/home/*"
                        onBlur={(event) => {
                          const nextUrls = [...displayUrls]
                          nextUrls[index] = normalizeUrlPathname(event.target.value)
                          setDisplayUrls(nextUrls)
                        }}
                        onChange={(event) => {
                          const nextUrls = [...displayUrls]
                          nextUrls[index] = event.target.value
                          setDisplayUrls(nextUrls)
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        aria-label={t("campaigns.templates.widget.config.displayUrls.remove")}
                        onClick={() => setDisplayUrls(displayUrls.filter((_, i) => i !== index))}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-fit gap-2"
                    onClick={() => setDisplayUrls([...displayUrls, "/"])}
                  >
                    <Plus size={16} />
                    {t("campaigns.templates.widget.config.displayUrls.add")}
                  </Button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  )
}
